/**
 * system domain — typed API client.
 *
 * Mirrors the `api.*` helpers in liff/api.js for the platform/system surface:
 * data templates + tenant reset, universal preset libraries, super-admin
 * (tenants / AI usage / benchmark coverage + import), the notification inbox +
 * settings, the manual IDP-reminder trigger, and the AI Co-Pilot endpoints
 * (brief / ask / setup-suggest / assessment advisor).
 *
 * apiFetch already unwraps the ApiResponse envelope's `.data`, so helpers below
 * return the inner payload directly. *Url helpers return a STRING (vanilla
 * builds a download URL, not a fetch). The benchmark import posts a raw xlsx
 * binary with an explicit Content-Type, which can't go through apiFetch's
 * JSON.stringify path — it is replicated as a direct fetch.
 */
import { ApiError, apiFetch, buildUrl, getApiBase, qs } from '../lib/api-client';
import { useAuthStore } from '../lib/store';
import type { ApiResponse } from '@humanai/shared';

// ── Entities ──────────────────────────────────────────────────────────────────

/** A pre-built tenant data template (industry/seed bundle) selectable on setup. */
export interface SystemTemplate {
  id: string;
  name?: string;
  industry?: string;
  description?: string;
  [k: string]: unknown;
}

/**
 * GET /api/v1/templates payload. The worker returns an OBJECT (not an array):
 * `{ industries: SystemTemplate[], universal_modules: {…counts} }`. Typing the
 * helper as `SystemTemplate[]` was a lie that would crash any caller doing
 * `data.map(...)` (truthy non-array → `?? []` never kicks in). The page reads
 * BOTH `industries` and `universal_modules`, so we surface the whole object.
 */
export interface TemplatesPayload {
  industries: SystemTemplate[];
  universal_modules: Record<string, number>;
  [k: string]: unknown;
}

/** A single universal preset entry (ai-prompt / 360 item / IDP stage / etc.). */
export interface UniversalPreset {
  code?: string;
  label?: string;
  [k: string]: unknown;
}

/**
 * Most universal-preset endpoints return a keyed OBJECT (grouped by role /
 * stage / preset-code), not a flat array. This type models that wrapper so the
 * helpers don't pretend to return `UniversalPreset[]` (which would white-screen
 * a `.map` caller). Callers `Object.values()` or pick a known key.
 */
export type UniversalPresetMap = Record<string, unknown>;

/** Result of applying a template to the current tenant. */
export interface TemplateApplyResult {
  applied?: boolean;
  counts?: Record<string, number>;
  [k: string]: unknown;
}

/** Result of resetting tenant data (mode-scoped wipe + reseed). */
export interface TenantResetResult {
  reset?: boolean;
  mode?: string;
  [k: string]: unknown;
}

/** Reset mode (how much tenant data to clear). */
export type TenantResetMode = string;

/** A tenant row in the super-admin tenant directory. */
export interface AdminTenant {
  id: string;
  name?: string;
  plan?: string;
  status?: string;
  created_at?: string;
  [k: string]: unknown;
}

/** Aggregated AI token/cost usage over a window for the admin console. */
export interface AdminAiUsage {
  days?: number;
  total_tokens?: number;
  total_cost?: number;
  by_feature?: Record<string, unknown>;
  [k: string]: unknown;
}

/** Salary-benchmark dataset coverage report (admin). */
export interface AdminBenchmarkCoverage {
  positions_covered?: number;
  positions_total?: number;
  [k: string]: unknown;
}

/** Result of importing a benchmark xlsx (rows ingested / archived). */
export interface BenchmarkImportResult {
  imported?: number;
  archived?: number;
  [k: string]: unknown;
}

/** Strategy for what to do with the previous benchmark dataset on import. */
export type BenchmarkImportStrategy = 'archive_old' | string;

/** An inbox notification item. */
export interface Notification {
  id: string;
  title?: string;
  body?: string;
  category?: string;
  read_at?: string | null;
  created_at?: string;
  link?: string;
  [k: string]: unknown;
}

/** Unread-count summary for the notification bell badge. */
export interface UnreadCount {
  count: number;
  [k: string]: unknown;
}

/** Tenant-scope notification routing/channel settings (OWNER/HR). */
export interface NotificationSettings {
  [k: string]: unknown;
}

/** Result of a manual admin IDP-reminder run. */
export interface IdpReminderRunResult {
  sent?: number;
  [k: string]: unknown;
}

// ── AI Co-Pilot shapes ──────────────────────────────────────────────────────────

/** The AI Co-Pilot daily brief payload (role-scoped cards/insights). */
export interface CopilotBrief {
  cards?: unknown[];
  generated_at?: string;
  [k: string]: unknown;
}

/** Free-form Co-Pilot ask request (a question + optional context). */
export interface CopilotAskBody {
  message?: string;
  [k: string]: unknown;
}

/** Co-Pilot answer payload. */
export interface CopilotAnswer {
  answer?: string;
  [k: string]: unknown;
}

/** Setup-suggest request (what the tenant has so far → next-step suggestions). */
export interface SetupSuggestBody {
  [k: string]: unknown;
}

/** Setup-suggest answer (recommended next setup actions). */
export interface SetupSuggestResult {
  suggestions?: unknown[];
  [k: string]: unknown;
}

/** A single chat turn for the multi-turn assessment advisor. */
export interface AdvisorMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  [k: string]: unknown;
}

/** Assessment advisor reply (recommended methods/modules/templates). */
export interface AdvisorReply {
  reply?: string;
  messages?: AdvisorMessage[];
  [k: string]: unknown;
}

// ── Query shapes ─────────────────────────────────────────────────────────────────

export type AdminTenantsParams = Record<string, string | number | boolean | null | undefined>;
export type NotificationListParams = Record<string, string | number | boolean | null | undefined>;

// ── Raw-binary benchmark import (replicates api.js adminBenchmarkImport) ──────────
// The endpoint expects the spreadsheet bytes as the request body with an xlsx
// Content-Type — not multipart and not JSON — so it bypasses apiFetch. We read
// the file to an ArrayBuffer and POST the bytes directly, like api.js does.
const XLSX_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

async function postBenchmarkImport(
  file: Blob,
  strategy: BenchmarkImportStrategy = 'archive_old',
): Promise<BenchmarkImportResult> {
  const jwt = useAuthStore.getState().jwt;
  const buf = await file.arrayBuffer();
  const headers: Record<string, string> = { 'Content-Type': XLSX_CONTENT_TYPE };
  if (jwt) headers.Authorization = `Bearer ${jwt}`;

  let res: Response;
  try {
    res = await fetch(
      buildUrl(`/api/v1/admin/benchmarks/import?strategy=${encodeURIComponent(strategy)}`),
      { method: 'POST', headers, body: buf },
    );
  } catch (e) {
    throw new ApiError('NETWORK', 'อัปโหลดไฟล์ไม่ได้ กรุณาเช็คอินเทอร์เน็ต', 0, e);
  }
  const json = (await res.json()) as ApiResponse<BenchmarkImportResult>;
  if (!json.ok) throw new ApiError(json.code, json.error, res.status, json.details);
  return json.data;
}

// ── API ─────────────────────────────────────────────────────────────────────────

export const systemApi = {
  // Templates
  // GET /api/v1/templates → worker returns `{ industries, universal_modules }`
  // (an OBJECT, not an array). Typed as TemplatesPayload so callers don't
  // `.map` a non-array and white-screen.
  listTemplates: (): Promise<TemplatesPayload> =>
    apiFetch<TemplatesPayload>('/api/v1/templates'),
  getTemplate: (id: string): Promise<SystemTemplate> =>
    apiFetch<SystemTemplate>(`/api/v1/templates/${id}`),
  applyTemplate: (body: Record<string, unknown>): Promise<TemplateApplyResult> =>
    apiFetch<TemplateApplyResult>('/api/v1/templates/apply', { method: 'POST', body }),
  resetTenant: (mode: TenantResetMode): Promise<TenantResetResult> =>
    apiFetch<TenantResetResult>('/api/v1/templates/reset', {
      method: 'POST',
      body: { mode, confirm: 'RESET-CONFIRM' },
    }),

  // Universal preset libraries
  // ai-prompts → worker returns `data: AI_QUICK_PROMPTS[role]` which IS a flat
  // array, so this one stays array-typed (verified array-OK).
  templateAiPrompts: (): Promise<UniversalPreset[]> =>
    apiFetch<UniversalPreset[]>('/api/v1/templates/universal/ai-prompts'),
  // The remaining universal libraries return keyed OBJECTS (grouped-by-key),
  // NOT arrays — e.g. feedback360 → `{ questionnaires:{…}, reviewer_kinds:{…} }`,
  // idp-stages → IDP_TEMPLATES (keyed), onboarding → `{ onboarding, manager_routine,
  // hr_cycle, sales_routine }`, jd-presets → JD_PRESETS (keyed). Typing them as
  // `UniversalPreset[]` was a lie that would crash any caller doing `.map`. They
  // surface the raw object (`UniversalPresetMap`); callers `Object.values()` /
  // pick keys explicitly.
  templateFeedback360: (): Promise<UniversalPresetMap> =>
    apiFetch<UniversalPresetMap>('/api/v1/templates/universal/feedback360'),
  templateIdpStages: (): Promise<UniversalPresetMap> =>
    apiFetch<UniversalPresetMap>('/api/v1/templates/universal/idp-stages'),
  templateOnboarding: (): Promise<UniversalPresetMap> =>
    apiFetch<UniversalPresetMap>('/api/v1/templates/universal/onboarding'),
  templateJdPresets: (): Promise<UniversalPresetMap> =>
    apiFetch<UniversalPresetMap>('/api/v1/templates/universal/jd-presets'),

  // Super-admin
  adminTenants: (params: AdminTenantsParams = {}): Promise<AdminTenant[]> =>
    apiFetch<AdminTenant[]>(`/api/v1/admin/tenants${qs(params)}`),
  adminAiUsage: (days: number = 30): Promise<AdminAiUsage> =>
    apiFetch<AdminAiUsage>(`/api/v1/admin/ai-usage${qs({ days })}`),
  adminCoverage: (): Promise<AdminBenchmarkCoverage> =>
    apiFetch<AdminBenchmarkCoverage>('/api/v1/admin/benchmarks/coverage'),
  /** Returns a STRING (vanilla builds a download URL, not a fetch). */
  adminBenchmarkTemplate: (): string =>
    `${getApiBase()}/api/v1/admin/benchmarks/template`,
  adminBenchmarkImport: (
    file: Blob,
    strategy: BenchmarkImportStrategy = 'archive_old',
  ): Promise<BenchmarkImportResult> => postBenchmarkImport(file, strategy),

  // Notifications · inbox
  listNotifications: (params: NotificationListParams = {}): Promise<Notification[]> =>
    apiFetch<Notification[]>(`/api/v1/notifications${qs(params)}`),
  markNotificationRead: (id: string): Promise<void> =>
    apiFetch<void>(`/api/v1/notifications/${id}/read`, { method: 'POST' }),
  markAllNotificationsRead: (): Promise<void> =>
    apiFetch<void>('/api/v1/notifications/read-all', { method: 'POST' }),
  getUnreadCount: (): Promise<UnreadCount> =>
    apiFetch<UnreadCount>('/api/v1/notifications/unread-count'),

  // Notification settings (tenant-scope · OWNER/HR)
  getNotificationSettings: (): Promise<NotificationSettings> =>
    apiFetch<NotificationSettings>('/api/v1/notification-settings'),
  updateNotificationSettings: (
    body: Partial<NotificationSettings>,
  ): Promise<NotificationSettings> =>
    apiFetch<NotificationSettings>('/api/v1/notification-settings', {
      method: 'PATCH',
      body,
    }),

  // Admin trigger — manual IDP reminder run (OWNER/SUPER_ADMIN)
  runIdpReminders: (): Promise<IdpReminderRunResult> =>
    apiFetch<IdpReminderRunResult>('/api/v1/admin/run-idp-reminders', { method: 'POST' }),

  // AI Co-Pilot
  copilotBrief: (refresh: boolean = false): Promise<CopilotBrief> =>
    apiFetch<CopilotBrief>(`/api/v1/ai/copilot/brief${refresh ? '?refresh=1' : ''}`),
  copilotAsk: (body: CopilotAskBody): Promise<CopilotAnswer> =>
    apiFetch<CopilotAnswer>('/api/v1/ai/copilot/ask', { method: 'POST', body }),
  setupSuggest: (body: SetupSuggestBody): Promise<SetupSuggestResult> =>
    apiFetch<SetupSuggestResult>('/api/v1/ai/setup-suggest', { method: 'POST', body }),

  // AI Assessment Advisor (multi-turn chat — recommend methods/modules/templates)
  askAdvisor: (messages: AdvisorMessage[], reset: boolean = false): Promise<AdvisorReply> =>
    apiFetch<AdvisorReply>('/api/v1/ai/assess-advisor', {
      method: 'POST',
      body: { messages, reset },
    }),
};
